/**
 * aiChatStore — Zustand store for per-tab AI chat state.
 *
 * Each tab (query or notebook) has its own independent chat state.
 */

import { create } from 'zustand';
import type { AiChatMessage, AiChatTabState, AiChatPhase, AiChatRunMetadata } from '../types';
import { flushMessageCounts } from '../services/messageCounter';

export const DEFAULT_SIDEBAR_WIDTH = 350;

export const defaultTabState = (): AiChatTabState => ({
  messages: [],
  sessionId: null,
  isLoading: false,
  error: null,
  sidebarOpen: false,
  sidebarWidth: DEFAULT_SIDEBAR_WIDTH,
  phase: 'idle',
  statusText: null,
  lastRunMetadata: null,
  activeAdapter: null,
});

type AiChatState = {
  tabs: Record<string, AiChatTabState>;

  getTabState: (tabId: string) => AiChatTabState;
  /**
   * `options.silent` skips the `feature:ai_chatbot` event — for callers that
   * already fire their own activation event (the AI task launchers), so one
   * launch is counted once instead of landing in two series.
   */
  setSidebarOpen: (tabId: string, open: boolean, options?: { silent?: boolean }) => void;
  toggleSidebar: (tabId: string) => void;
  setSidebarWidth: (tabId: string, width: number) => void;
  addMessage: (tabId: string, message: AiChatMessage) => void;
  updateLastAssistantMessage: (tabId: string, updater: (msg: AiChatMessage) => AiChatMessage) => void;
  setLoading: (tabId: string, loading: boolean) => void;
  setError: (tabId: string, error: string | null) => void;
  setSessionId: (tabId: string, sessionId: string | null) => void;
  setPhase: (tabId: string, phase: AiChatPhase) => void;
  setStatusText: (tabId: string, statusText: string | null) => void;
  setLastRunMetadata: (tabId: string, metadata: AiChatRunMetadata | null) => void;
  setMessages: (tabId: string, messages: AiChatMessage[]) => void;
  setActiveAdapter: (tabId: string, adapterName: string | null) => void;
  switchAdapter: (tabId: string, adapterName: string) => void;
  clearMessages: (tabId: string) => void;
  removeTab: (tabId: string) => void;
};

export const useAiChatStore = create<AiChatState>((set, get) => ({
  tabs: {},

  getTabState: (tabId) => {
    return get().tabs[tabId] ?? defaultTabState();
  },

  setSidebarOpen: (tabId, open, options) => {
    if (open && !options?.silent) {
      const adapter = get().tabs[tabId]?.activeAdapter ?? 'unknown';
      if (typeof window !== 'undefined') {
        window.electronAPI?.analytics?.track('feature:ai_chatbot', { adapter }).catch(() => {});
      }
    }
    set((state) => ({
      tabs: {
        ...state.tabs,
        [tabId]: { ...(state.tabs[tabId] ?? defaultTabState()), sidebarOpen: open },
      },
    }));
  },

  toggleSidebar: (tabId) => {
    const current = get().tabs[tabId] ?? defaultTabState();
    set((state) => ({
      tabs: {
        ...state.tabs,
        [tabId]: { ...current, sidebarOpen: !current.sidebarOpen },
      },
    }));
  },

  setSidebarWidth: (tabId, width) => {
    set((state) => ({
      tabs: {
        ...state.tabs,
        [tabId]: { ...(state.tabs[tabId] ?? defaultTabState()), sidebarWidth: width },
      },
    }));
  },

  addMessage: (tabId, message) => {
    set((state) => {
      const tab = state.tabs[tabId] ?? defaultTabState();
      return {
        tabs: {
          ...state.tabs,
          [tabId]: { ...tab, messages: [...tab.messages, message] },
        },
      };
    });
  },

  updateLastAssistantMessage: (tabId, updater) => {
    set((state) => {
      const tab = state.tabs[tabId] ?? defaultTabState();
      const msgs = [...tab.messages];
      let found = false;
      for (let i = msgs.length - 1; i >= 0; i--) {
        if (msgs[i].role === 'assistant') {
          msgs[i] = updater(msgs[i]);
          found = true;
          break;
        }
      }
      if (!found) {
        console.warn(`[AI Chat Store] updateLastAssistantMessage: NO assistant message found for tab ${tabId}! Messages:`, msgs.map(m => m.role));
      }
      return { tabs: { ...state.tabs, [tabId]: { ...tab, messages: msgs } } };
    });
  },

  setLoading: (tabId, loading) => {
    set((state) => ({
      tabs: {
        ...state.tabs,
        [tabId]: { ...(state.tabs[tabId] ?? defaultTabState()), isLoading: loading },
      },
    }));
  },

  setError: (tabId, error) => {
    set((state) => ({
      tabs: {
        ...state.tabs,
        [tabId]: { ...(state.tabs[tabId] ?? defaultTabState()), error },
      },
    }));
  },

  setSessionId: (tabId, sessionId) => {
    set((state) => ({
      tabs: {
        ...state.tabs,
        [tabId]: { ...(state.tabs[tabId] ?? defaultTabState()), sessionId },
      },
    }));
  },

  setPhase: (tabId, phase) => {
    set((state) => ({
      tabs: {
        ...state.tabs,
        [tabId]: { ...(state.tabs[tabId] ?? defaultTabState()), phase },
      },
    }));
  },

  setStatusText: (tabId, statusText) => {
    set((state) => ({
      tabs: {
        ...state.tabs,
        [tabId]: { ...(state.tabs[tabId] ?? defaultTabState()), statusText },
      },
    }));
  },

  setLastRunMetadata: (tabId, metadata) => {
    set((state) => ({
      tabs: {
        ...state.tabs,
        [tabId]: { ...(state.tabs[tabId] ?? defaultTabState()), lastRunMetadata: metadata },
      },
    }));
  },

  setMessages: (tabId, messages) => {
    set((state) => ({
      tabs: {
        ...state.tabs,
        [tabId]: { ...(state.tabs[tabId] ?? defaultTabState()), messages },
      },
    }));
  },

  setActiveAdapter: (tabId, adapterName) => {
    set((state) => ({
      tabs: {
        ...state.tabs,
        [tabId]: { ...(state.tabs[tabId] ?? defaultTabState()), activeAdapter: adapterName },
      },
    }));
  },

  switchAdapter: (tabId, adapterName) => {
    // Flush pending chat message counts for the outgoing adapter before the
    // switch so the count attaches to the right adapter.
    void flushMessageCounts();
    if (typeof window !== 'undefined') {
      window.electronAPI?.analytics?.track('feature:ai_chatbot_change', { adapter: adapterName }).catch(() => {});
    }
    set((state) => {
      const tab = state.tabs[tabId] ?? defaultTabState();
      return {
        tabs: {
          ...state.tabs,
          [tabId]: {
            ...tab,
            messages: [],
            sessionId: null,
            error: null,
            phase: 'idle',
            statusText: null,
            lastRunMetadata: null,
            activeAdapter: adapterName,
          },
        },
      };
    });
    // Persist preference globally
    if (typeof window !== 'undefined') {
      window.electronAPI?.aiChat?.setPreferredAdapter(adapterName).catch(() => { /* ignore */ });
    }
  },

  clearMessages: (tabId) => {
    set((state) => ({
      tabs: {
        ...state.tabs,
        [tabId]: {
          ...(state.tabs[tabId] ?? defaultTabState()),
          messages: [],
          sessionId: null,
          error: null,
          phase: 'idle',
          statusText: null,
          lastRunMetadata: null,
        },
      },
    }));
  },

  removeTab: (tabId) => {
    set((state) => {
      const { [tabId]: _, ...rest } = state.tabs;
      void _;
      return { tabs: rest };
    });
  },
}));
